import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#labelColorThumbnails();
    requestAnimationFrame(() => {
      requestAnimationFrame(this.#syncGalleryToSelectedColor);
    });
  }

  #controller = new AbortController();

  /**
   * Corrects the base slideshow's initial-position race after a variant section
   * is rendered so the checked color and the large image always agree.
   */
  #syncGalleryToSelectedColor = async () => {
    const map = this.colorMediaMap;
    const slideshow = this.slideshow;
    const section = this.closest('.shopify-section');
    const checkedColor = section?.querySelector(
      'variant-picker fieldset[data-option-name="color"] input[type="radio"]:checked'
    );

    if (!map || !slideshow || !(checkedColor instanceof HTMLInputElement)) return;

    const item = map.items.find(
      (candidate) => String(candidate.optionValueId) === String(checkedColor.dataset.optionValueId)
    );
    const slides = slideshow.refs.slides;
    const targetIndex = slides?.findIndex((slide) => String(slide.getAttribute('slide-id')) === String(item?.mediaId));

    if (!item || !slides?.length || targetIndex == null || targetIndex < 0) return;

    // The base slideshow sets `current` before it performs the initial scroll.
    // Resetting it lets select() perform that scroll instead of returning early.
    if (slideshow.current === targetIndex && targetIndex !== 0) slideshow.current = 0;

    await slideshow.select({ id: String(item.mediaId) }, undefined, { animate: false });

    for (const [index, slide] of slides.entries()) {
      slide.setAttribute('aria-hidden', String(index !== targetIndex));
    }
  };

  /**
   * Maps each product media item to its color option and a valid fallback variant.
   * @returns {{ productUrl: string, optionName: string, items: Array<{ color: string, optionValueId: string|number, variantId: string|number, mediaId: string|number, options: string[] }> } | null}
   */
  get colorMediaMap() {
    const script = this.querySelector('script[data-color-media-map]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Unable to read product color media map', error);
      return null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Keeps the color swatches and image thumbnails as two synchronized controls.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail.userInitiated || event.detail.id == null) return;

    const map = this.colorMediaMap;
    const item = map?.items.find((candidate) => String(candidate.mediaId) === String(event.detail.id));
    if (!map || !item) return;

    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker');
    if (!picker) return;

    const colorInputs = Array.from(
      picker.querySelectorAll('fieldset[data-option-name="color"] input[type="radio"]')
    );
    const colorInput = colorInputs.find(
      (input) => input instanceof HTMLInputElement && String(input.dataset.optionValueId) === String(item.optionValueId)
    );

    if (!(colorInput instanceof HTMLInputElement) || colorInput.checked) return;

    const canUseCurrentSize =
      colorInput.dataset.optionAvailable === 'true' && colorInput.getAttribute('aria-disabled') !== 'true';

    if (canUseCurrentSize) {
      colorInput.click();
      return;
    }

    this.#selectFallbackVariant(picker, colorInput, item, map.productUrl);
  };

  /**
   * Selects a valid size/color combination when the requested color is unavailable
   * for the shopper's current size (for example Natural with size S).
   * @param {HTMLElement & { updateSelectedOption?: (target: Element) => void }} picker
   * @param {HTMLInputElement} colorInput
   * @param {{ variantId: string|number, options: string[] }} item
   * @param {string} productUrl
   */
  #selectFallbackVariant(picker, colorInput, item, productUrl) {
    const fieldsets = Array.from(picker.querySelectorAll('fieldset[data-fieldset-index]'));
    const selectedInputs = [];

    item.options.forEach((optionValue, index) => {
      const fieldset = fieldsets[index];
      if (!fieldset) return;

      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (candidate) => candidate instanceof HTMLInputElement && candidate.value === optionValue
      );

      if (!(input instanceof HTMLInputElement)) return;
      picker.updateSelectedOption?.(input);
      selectedInputs.push(input);
    });

    if (selectedInputs.length !== fieldsets.length) {
      this.#navigateToVariant(productUrl, item.variantId);
      return;
    }

    colorInput.dataset.variantId = String(item.variantId);
    colorInput.dataset.optionAvailable = 'true';
    colorInput.removeAttribute('aria-disabled');
    colorInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Provides a fail-safe exact variant selection if the live picker structure is unexpected.
   * @param {string} productUrl
   * @param {string|number} variantId
   */
  #navigateToVariant(productUrl, variantId) {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    window.location.assign(url.toString());
  }

  /** Gives each thumbnail a useful color-specific accessible label. */
  #labelColorThumbnails() {
    const map = this.colorMediaMap;
    if (!map) return;

    for (const thumbnail of this.querySelectorAll('[data-media-id]')) {
      const item = map.items.find(
        (candidate) => String(candidate.mediaId) === String(thumbnail.getAttribute('data-media-id'))
      );
      if (item) thumbnail.setAttribute('aria-label', `Select ${item.color} shirt`);
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
